import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { HelpCircle } from 'lucide-react';
import { API_URLS } from '../../config/api';
import MediaDisplay from '../MediaDisplay';
import { formatPrice, safeParseFloat } from '../../utils/format';
import * as Icons from 'lucide-react';
import {
  Server,
  Cpu,
  HardDrive,
  Network,
  CheckCircle,
  Crown,
  ArrowRight,
  Shield,
  Terminal,
  Zap,
  Globe,
  Database,
  BarChart3,
  Activity,
  RotateCcw,
  Square,
  Play,
  Users,
  Mail,
  MessageCircle,
  Clock,
  Award,
  X as XIcon,
  BookOpen,
} from 'lucide-react';

const VDSServerPage = () => {
  const [pageContent, setPageContent] = useState(null);
  const additional = pageContent?.additional_sections || {};
  const featuresSection = additional.features_section || {};
  const featuresCards = Array.isArray(featuresSection.cards) ? featuresSection.cards : [];
  const standardSection = additional.standard_section || {};
  const standardCards = Array.isArray(standardSection.cards) ? standardSection.cards : [];
  const valueProductsSection = additional.value_products_section || {};
  const valueProductCards = Array.isArray(valueProductsSection.cards) ? valueProductsSection.cards : [];
  const [vdsPlans, setVdsPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [faqs, setFaqs] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('905324404011');
  const [contentReady, setContentReady] = useState(false);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await fetch(API_URLS.PAGE_CONTENT('vds-sunucu'));
      const json = await response.json();
      if (json.status === 'success' && json.data) {
        const additionalRaw = json.data.additional_sections;
        let additional = {};
        if (typeof additionalRaw === 'string' && additionalRaw.trim().startsWith('{')) {
          try {
            additional = JSON.parse(additionalRaw);
          } catch (error) {
            console.error('VDS additional_sections parse error:', error);
          }
        } else if (additionalRaw && typeof additionalRaw === 'object') {
          additional = additionalRaw;
        }
        setPageContent({
          ...json.data,
          additional_sections: additional
        });
      }
    } catch (error) {
      console.error('VDS sayfa içeriği alınamadı:', error);
      setPageContent(null);
    }
  };

  useEffect(() => {
    fetchPageContent();
    fetchVdsPackages();
    fetchFaqs();
    fetchBlogs();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCompanySettings`);
      if (res.ok) {
        const json = await res.json();
        if (json.status === 'success' && json.data?.whatsapp) {
          setWhatsappNumber(json.data.whatsapp);
        }
      }
    } catch (e) {
      console.error('WhatsApp numarası alınamadı:', e);
    }
  };

  const fetchVdsPackages = async () => {
    try {
      setLoading(true);
      // Reuse VPS endpoint until a dedicated VDS endpoint exists
      const res = await fetch(API_URLS.VPS_PACKAGES);
      const data = await res.json();
      if (Array.isArray(data)) {
        const formatted = data.map(pkg => {
          // Parse features - obje yapısını koru
          let rawFeatures = Array.isArray(pkg.features) ? pkg.features : [];
          const parsedFeatures = rawFeatures.map(f => {
            if (typeof f === 'string') {
              return { name: f, text: f, included: true };
            }
            if (f && typeof f === 'object') {
              return {
                name: f.name || f.text || '',
                text: f.text || f.name || '',
                included: f.included !== false
              };
            }
            return { name: String(f), text: String(f), included: true };
          });
          
          return {
            id: pkg.id,
            name: pkg.name?.replace(/VPS/gi, 'VDS') || pkg.name || 'VDS',
            cpu: { cores: pkg.cpu_cores, model: pkg.cpu_model },
            ram: pkg.ram,
            storage: pkg.storage,
            bandwidth: pkg.bandwidth,
            price: safeParseFloat(pkg.price),
            originalPrice: safeParseFloat(pkg.original_price),
            discount: pkg.discount,
            color: pkg.color || 'from-blue-500 to-cyan-500',
            bgColor: pkg.bg_color || 'bg-blue-50',
            popular: pkg.is_popular == 1 || pkg.is_popular === '1' || pkg.is_popular === true,
            features: parsedFeatures
          };
        });
        setVdsPlans(formatted);
      } else {
        setVdsPlans(fallbackVdsPlans);
      }
    } catch (e) {
      setVdsPlans(fallbackVdsPlans);
    } finally {
      setLoading(false);
    }
  };

  const normalize = (s) => (s || '').toString().toLowerCase()
    .replace(/ı/g,'i').replace(/ü/g,'u').replace(/ö/g,'o')
    .replace(/ş/g,'s').replace(/ç/g,'c').replace(/ğ/g,'g');

  const fetchFaqs = async () => {
    try {
      const res = await fetch(API_URLS.FAQ);
      const text = await res.text();
      let data; try { data = JSON.parse(text); } catch { data = []; }

      if (Array.isArray(data?.categories) && Array.isArray(data?.questions)) {
        const cat = data.categories.find(c => {
          const title = normalize(c?.title);
          const slug = normalize(c?.slug);
          return title === 'vds sunucu' || slug === 'vds-sunucu' || title.includes('vds');
        });
        const filtered = data.questions
          .filter(q => q.is_active == 1 && (cat ? q.category_id === cat.id : false))
          .sort((a,b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          .map(q => ({ question: q.question || 'Soru', answer: q.answer || '' }));
        setFaqs(filtered);
        return;
      }

      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      const flat = list.filter(item => {
        const candidates = [];
        if (item.categories) candidates.push(...(Array.isArray(item.categories) ? item.categories : [item.categories]));
        if (item.category) candidates.push(...(Array.isArray(item.category) ? item.category : [item.category]));
        if (item.category_title) candidates.push(item.category_title);
        if (item.categoryTitle) candidates.push(item.categoryTitle);
        if (item.category_name) candidates.push(item.category_name);
        return candidates.some(c => normalize(c?.title || c).includes('vds sunucu'));
      }).map(f => ({ question: f.question || f.title || 'Soru', answer: f.answer || f.content || '' }));
      setFaqs(flat);
    } catch (e) {
      setFaqs([]);
    }
  };

  const fetchBlogs = async () => {
    try {
      setBlogsLoading(true);
      // Try server-side filtered fetch first
      const res = await fetch(`${API_URLS.BLOGS}&category=vds-sunucu&limit=3`);
      const text = await res.text();
      let data; try { data = JSON.parse(text); } catch { data = { blogs: [] }; }
      let list = Array.isArray(data?.blogs) ? data.blogs : Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

      // Fallback: fetch all then client-side filter if empty
      if (!list || list.length === 0) {
        const allRes = await fetch(API_URLS.BLOGS);
        const allText = await allRes.text();
        let allData; try { allData = JSON.parse(allText); } catch { allData = { blogs: [] }; }
        const allList = Array.isArray(allData?.blogs) ? allData.blogs : Array.isArray(allData?.data) ? allData.data : Array.isArray(allData) ? allData : [];
        
        list = allList.filter((b) => {
          const candidates = [];
          if (b.categories) candidates.push(...(Array.isArray(b.categories) ? b.categories : [b.categories]));
          if (b.category) candidates.push(...(Array.isArray(b.category) ? b.category : [b.category]));
          if (b.category_title) candidates.push(b.category_title);
          if (b.categoryTitle) candidates.push(b.categoryTitle);
          if (b.category_name) candidates.push(b.category_name);
          return candidates.some(c => normalize(c?.title || c).includes('vds sunucu') || normalize(c?.title || c).includes('vds-sunucu'));
        }).slice(0, 3);
      }

      const filtered = list
        .sort((a,b) => (new Date(b.created_at||b.createdAt||0)) - (new Date(a.created_at||a.createdAt||0)) || (b.id||0)-(a.id||0))
        .slice(0,3)
        .map(b => ({
            id: b.id,
            title: b.title || b.name || 'Blog',
            excerpt: b.excerpt || b.summary || '',
            image: b.image || b.cover || b.thumbnail || '',
            slug: b.slug || b.id,
            href: b.slug ? `/blog/${b.slug}` : '#',
            tag: b.category_name || b.category || 'VDS Sunucu'
          }));

      setBlogs(filtered);
    } catch (e) {
      console.error('Blogs fetch failed:', e);
      setBlogs([]);
    } finally {
      setBlogsLoading(false);
    }
  };

  const fallbackVdsPlans = [
    { id: 'vds-1', name: 'VDS-1', cpu: { cores: 2, model: 'Intel® E5 Serisi V4' }, ram: '4 GB', storage: '50 GB', bandwidth: '100 Mbit', price: 299, originalPrice: 399, discount: 25, color: 'from-blue-500 to-cyan-500', bgColor: 'bg-blue-50', popular: false, features: [
      { name: 'Dedike Kaynaklar', text: 'Dedike Kaynaklar', included: true },
      { name: 'Enterprise SSD', text: 'Enterprise SSD', included: true },
      { name: '%99,9 Uptime', text: '%99,9 Uptime', included: true },
      { name: 'İstanbul Lokasyon', text: 'İstanbul Lokasyon', included: true }
    ]},
    { id: 'vds-2', name: 'VDS-2', cpu: { cores: 4, model: 'Intel® E5 Serisi V4' }, ram: '8 GB', storage: '100 GB', bandwidth: '100 Mbit', price: 549, originalPrice: 729, discount: 25, color: 'from-purple-500 to-pink-500', bgColor: 'bg-purple-50', popular: true, features: [
      { name: 'Dedike Kaynaklar', text: 'Dedike Kaynaklar', included: true },
      { name: 'Enterprise SSD', text: 'Enterprise SSD', included: true },
      { name: '%99,9 Uptime', text: '%99,9 Uptime', included: true },
      { name: 'Yedekli Operatör', text: 'Yedekli Operatör', included: true }
    ]},
    { id: 'vds-3', name: 'VDS-3', cpu: { cores: 8, model: 'Intel® E5 Serisi V4' }, ram: '16 GB', storage: '200 GB', bandwidth: '100 Mbit', price: 999, originalPrice: 1299, discount: 23, color: 'from-green-500 to-emerald-500', bgColor: 'bg-green-50', popular: false, features: [
      { name: 'Dedike Kaynaklar', text: 'Dedike Kaynaklar', included: true },
      { name: 'Enterprise SSD', text: 'Enterprise SSD', included: true },
      { name: '%99,9 Uptime', text: '%99,9 Uptime', included: true },
      { name: 'DDoS Koruması', text: 'DDoS Koruması', included: true }
    ]},
    { id: 'vds-4', name: 'VDS-4', cpu: { cores: 16, model: 'Intel® E5 Serisi V4' }, ram: '32 GB', storage: '400 GB', bandwidth: '100 Mbit', price: 1799, originalPrice: 2299, discount: 22, color: 'from-orange-500 to-red-500', bgColor: 'bg-orange-50', popular: false, features: [
      { name: 'Dedike Kaynaklar', text: 'Dedike Kaynaklar', included: true },
      { name: 'Enterprise SSD', text: 'Enterprise SSD', included: true },
      { name: '%99,9 Uptime', text: '%99,9 Uptime', included: true },
      { name: 'IP/KVM Yönetimi', text: 'IP/KVM Yönetimi', included: true }
    ]},
  ];

  const OperatingSystems = [
    { id: 'linux', name: 'Linux', icon: Shield, color: 'from-blue-500 to-cyan-500', versions: ['Ubuntu 24.04','Debian 12','Alma 9','Rocky 9'] },
    { id: 'windows', name: 'Windows', icon: Terminal, color: 'from-purple-500 to-pink-500', versions: ['Windows Server 2022','Windows Server 2019'] },
  ];

  const VDSCard = ({ plan, index }) => (
    <Card className={`relative border-0 shadow-xl cursor-pointer ${plan.popular ? 'ring-4 ring-purple-200' : ''} bg-white`}>
      {plan.popular && (
        <div className="absolute top-0 transform -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: '52%' }}>
          <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 text-sm font-bold" style={{ width: '150px' }}>
            <Crown className="w-4 h-4 mr-1" />
            EN POPÜLER
          </Badge>
        </div>
      )}
      <CardHeader className="space-y-4 pb-6">
        <div className="space-y-4">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${plan.color} flex items-center justify-center`}>
            <Server className="w-8 h-8 text-white" />
          </div>
          <div className="space-y-2">
            <CardTitle className="text-2xl font-bold text-gray-900">{plan.name}</CardTitle>
            <p className="text-gray-600 leading-relaxed">Yüksek performanslı VDS sunucu çözümü</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <Cpu className="w-5 h-5 text-orange-600" />
            <div>
              <p className="font-semibold text-gray-900">{plan.cpu.cores} Çekirdek</p>
              <p className="text-xs text-gray-600">{plan.cpu.model}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <BarChart3 className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-semibold text-gray-900">{plan.ram}</p>
              <p className="text-xs text-gray-600">DDR4 RAM</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <HardDrive className="w-5 h-5 text-purple-600" />
            <div>
              <p className="font-semibold text-gray-900">{plan.storage}</p>
              <p className="text-xs text-gray-600">Enterprise SSD</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Network className="w-5 h-5 text-orange-600" />
            <div>
              <p className="font-semibold text-gray-900">{plan.bandwidth}</p>
              <p className="text-xs text-gray-600">Bant Genişliği</p>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-baseline space-x-2">
            <span className="text-4xl font-bold text-gray-900">{formatPrice(plan.price, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
            <span className="text-gray-600">/yıllık</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-lg text-gray-400 line-through">{formatPrice(plan.originalPrice, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
            <Badge variant="destructive">%{plan.discount} İndirim</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-800">Özellikler:</h4>
          <div className="grid grid-cols-2 gap-2">
            {plan.features.map((feature, idx) => {
              const isIncluded = feature.included !== false;
              // Güvenli string extraction
              const featureText = typeof feature === 'string'
                ? feature
                : (feature?.text || feature?.name || String(feature));
              return (
                <div key={idx} className="flex items-center space-x-2">
                  {isIncluded ? (
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <XIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  )}
                  <span className={`text-xs font-medium ${isIncluded ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                    {featureText}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <Button className="w-full h-12 font-semibold bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white">
          <span>SATIN AL</span>
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <section 
        className="bg-gradient-to-r from-orange-500 to-red-600 text-white py-20"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">VDS Sunucu ile <span className="text-yellow-300">Üstün Performans</span></h1>
              <p className="text-xl md:text-2xl mb-8">Sanallaştırma ve bulut teknolojisinin avantajlarıyla dedike kaynak, yüksek performans ve kolay yönetim bir arada.</p>
            </div>
            <div className="hidden lg:block">
              <div className="rounded-2xl bg-white/10 border border-white/10 overflow-hidden">
                <MediaDisplay
                  src={additional.hero?.image_url || pageContent?.hero_image_url || "https://via.placeholder.com/600x400/F97316/FFFFFF?text=VDS+Sunucu"}
                  alt="VDS Sunucu"
                  className="w-full h-full object-cover"
                  videoProps={{ controls: true }}
                  imgProps={{ loading: "lazy" }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section 
        className="py-16 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">VDS Sunucu <span className="text-orange-600">Paketleri</span></h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">İhtiyaçlarınıza uygun VDS paketlerini keşfedin. Dedike kaynaklar ve kurumsal düzeyde güvenilirlik.</p>
          </div>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {vdsPlans.map((plan, index) => (
                <VDSCard key={plan.id} plan={plan} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* VDS Server Nedir? */}
      <section 
        className="py-16 bg-gray-900"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center flex-col-reverse lg:grid">
            <div className="bg-gradient-to-r from-orange-700 to-red-700 rounded-lg overflow-hidden">
              <MediaDisplay
                src={additional.intro_section?.image_url || "https://via.placeholder.com/600x400/C2410C/FFFFFF?text=VDS+Server"}
                alt="VDS Server Nedir?"
                className="w-full h-full object-cover"
                videoProps={{ controls: true }}
                imgProps={{ loading: "lazy" }}
              />
            </div>
            <div className="text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">VDS Server Nedir?</h2>
              <p className="text-lg text-gray-200">
                VDS (Virtual Dedicated Server), ana sunucu kaynaklarının sanallaştırma teknolojisiyle donanımsal olarak bölündüğü ve her sunucunun birbirinden bağımsız olduğu yapıdır. 
                Tahsis edilen işlemci ve RAM kaynakları diğer kullanıcılarla paylaşılmaz; böylece tutarlı performans ve izolasyon sağlanır.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benzersiz VDS Server Özellikleri */}
      <section 
        className="py-16 bg-gray-100"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              {featuresSection.title || 'Benzersiz VDS Server Özellikleri'}
            </h2>
            <p className="text-gray-600">
              {featuresSection.subtitle || 'Yüksek performans ve tam kontrol sunan VDS avantajları'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(featuresCards.length > 0 ? featuresCards : [
              { icon: 'Shield', title: 'Dedike Sunucu Kaynağı', description: 'Donanım kaynaklarının dedike ayrıldığı yapı ile yüksek ve stabil performans.' },
              { icon: 'Globe', title: 'Kesintisiz & Yedekli Altyapı', description: 'Enerji, donanım ve network katmanında yedekli mimari ile süreklilik.' },
              { icon: 'Users', title: 'Ücretsiz Kontrol Paneli', description: 'CyberPanel ile sitelerinizi kolayca yönetin; dilediğiniz paneli kurun.' },
              { icon: 'Activity', title: 'Genişletilebilir Kaynaklar', description: 'İhtiyaca göre RAM, CPU ve disk kaynaklarını kolayca artırın.' },
              { icon: 'BarChart3', title: '%99,9 Uptime Garantisi', description: 'Ağ ve donanım SLA’leri ile kesintisiz erişim hedefi.' },
              { icon: 'HardDrive', title: 'Garantili Donanımlar', description: 'Arıza halinde donanım değişimi hızlı ve ücretsiz gerçekleştirilir.' }
            ]).map((feature, i) => {
              const IconComponent = Icons[feature.icon] || Shield;
              return (
                <Card key={`${feature.title || feature.text || 'feature'}-${i}`} className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                    </div>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Standart Özellikler */}
      <section 
        className="py-16 bg-gray-900"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-gradient-to-r from-orange-700 to-red-700 rounded-lg overflow-hidden">
              <MediaDisplay
                src={standardSection.image_url || 'https://via.placeholder.com/600x500/C2410C/FFFFFF?text=Standart+Özellikler'}
                alt={standardSection.title || 'Standart Özellikler'}
                className="w-full h-full object-cover"
                videoProps={{ controls: true }}
                imgProps={{ loading: "lazy" }}
              />
            </div>
            <div className="text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                {standardSection.title || 'Standart Özellikler'}
              </h2>
              <p className="text-gray-300 mb-8">
                {standardSection.subtitle || 'Etkin ve güvenli VDS sunucu yönetimine uygun standartlar'}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {(standardCards.length > 0 ? standardCards : [
                  { icon: 'Users', title: 'cPanel / Plesk' },
                  { icon: 'Terminal', title: 'IP/KVM' },
                  { icon: 'Shield', title: 'DDoS Koruması' },
                  { icon: 'Zap', title: '%99.9 Uptime' },
                  { icon: 'CheckCircle', title: 'Ücretsiz Kurulum' }
                ]).map((item, i) => {
                  const IconComponent = Icons[item.icon] || Shield;
                  return (
                    <Card key={`${item.title || 'standard'}-${i}`} className="bg-white/5 border-0">
                      <CardContent className="p-4">
                        <div className="w-10 h-10 rounded-xl bg-orange-600 mx-auto flex items-center justify-center mb-2">
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-white font-medium text-sm text-center">{item.title}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Sunucu ile İlgili Güncel İçerikler */}
      <section 
        className="py-16 bg-gray-100"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Sunucu ile İlgili Güncel İçerikler</h2>
            <p className="text-gray-600">Sunucu kullanımı ve yönetimi ile ilgili teknik anlatımlar</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {(blogs.length ? blogs : []).map((b) => {
              const hasValidImage = b.image && 
                b.image !== 'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=800&q=80' &&
                !b.image.includes('placeholder');
              
              return (
                <Card key={b.id} className="overflow-hidden border-orange-100 hover:shadow-lg transition-shadow">
                  {hasValidImage ? (
                    <div className="aspect-[16/9] bg-orange-50 overflow-hidden">
                      <MediaDisplay
                        src={b.image}
                        alt={b.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        videoProps={{ controls: true }}
                        imgProps={{
                          loading: "lazy",
                          onError: (e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center"><svg class="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center">
                      <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                  )}
                  <CardContent className="p-5 space-y-3">
                    <div className="inline-flex items-center px-3 py-1 bg-orange-50 text-orange-600 text-xs font-semibold rounded-full">
                      <BookOpen className="w-3.5 h-3.5 mr-1" />
                      {b.tag}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{b.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{b.excerpt || ''}</p>
                    <button
                      onClick={() => window.open(b.href, '_self')}
                      className="inline-flex items-center text-sm font-semibold text-orange-600 hover:text-orange-700"
                    >
                      Yazıyı Oku
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </CardContent>
                </Card>
              );
            })}
            {!blogsLoading && blogs.length === 0 && (
              <div className="col-span-3 text-center text-gray-500">Bu kategori için içerik bulunamadı.</div>
            )}
            {blogsLoading && (
              <div className="col-span-3 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* VDS Sunucunuza Değer Katacak Ürünler */}
      <section 
        className="py-16 bg-gradient-to-br from-orange-500 via-orange-600 to-red-600"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              {valueProductsSection.title || 'VDS Sunucunuza Değer Katacak Ürünler'}
            </h2>
            <p className="text-white/90">
              {valueProductsSection.subtitle || 'İşinize değer katan tamamlayıcı hizmetler'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(valueProductCards.length ? valueProductCards : [
              {
                title: 'SSL Sertifikası',
                description: 'Google’da yükselin, güvenliğinizi garanti altına alın.',
                image_url: 'https://cdn.natrocdn.com/ResourceFiles/v1/images/vds-server/ssl.png',
                link: '/ssl/ssl-sertifikasi'
              },
              {
                title: 'Premium Destek',
                description: 'Sunucu yönetimi için profesyonel uzman desteği alın.',
                image_url: 'https://cdn.natrocdn.com/ResourceFiles/v1/images/vds-server/support.png',
                link: '/sunucu-kiralama/sunucu-server-destek'
              },
            ]).map((product, index) => {
              const title = product.title || `Ürün ${index + 1}`;
              const description = product.description || product.desc || '';
              const image = product.image_url || product.img || '';
              const link = product.link || product.href || '';
              const cardContent = (
                <>
                  <div className="p-6 flex items-center justify-center">
                    {image ? (
                      <MediaDisplay
                        src={image}
                        alt={title}
                        className="h-20 object-contain"
                        videoProps={{ controls: true }}
                        imgProps={{ loading: "lazy" }}
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-full bg-orange-100" />
                    )}
                  </div>
                  <div className="px-6 pb-6">
                    <h3 className="font-semibold text-gray-900">{title}</h3>
                    <p className="text-gray-600 text-sm">{description}</p>
                  </div>
                </>
              );

              return link ? (
                <a
                  key={`${title}-${index}`}
                  href={link}
                  className="block bg-white rounded-lg shadow hover:shadow-lg overflow-hidden transition"
                >
                  {cardContent}
                </a>
              ) : (
                <div
                  key={`${title}-${index}`}
                  className="block bg-white rounded-lg shadow overflow-hidden"
                >
                  {cardContent}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section 
        className="py-16 bg-gradient-to-r from-orange-50 to-red-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center flex-col-reverse lg:grid">
            <div className="bg-gradient-to-r from-orange-100 to-red-100 rounded-lg overflow-hidden">
              <MediaDisplay
                src={additional.control_panel_section?.image_url || "https://via.placeholder.com/600x500/F97316/FFFFFF?text=Kontrol+Paneli"}
                alt="VDS Sunucu Kontrol Paneli"
                className="w-full h-full object-cover"
                videoProps={{ controls: true }}
                imgProps={{ loading: "lazy" }}
              />
            </div>
            <div className="text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">VDS Sunucu <span className="text-orange-600">Kontrol Paneli</span></h2>
              <p className="text-lg text-gray-600 mb-8">IP/KVM, DDoS koruması ve ücretsiz kurulum gibi güçlü yönetim özellikleri.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { icon: Shield, title: 'DDoS Koruması', desc: 'Gelişmiş saldırı önleme ve ağ güvenliği.' },
                  { icon: Terminal, title: 'IP/KVM Yönetimi', desc: 'Uzaktan konsol erişimi ile tam kontrol.' },
                  { icon: Globe, title: 'Uptime Garantisi', desc: '%99.9 ağ ve donanım sürekliliği.' },
                  { icon: Database, title: 'Enterprise SSD', desc: 'Yüksek IOPS ile hızlı depolama.' },
                ].map((f, idx) => (
                  <Card key={idx} className="border-0 shadow-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3 mb-2 justify-center lg:justify-start">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                          <f.icon className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="font-semibold text-gray-900 text-sm">{f.title}</h3>
                      </div>
                      <p className="text-xs text-gray-600 text-center lg:text-left">{f.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section 
        className="py-16 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">VDS Sunucu İçin <span className="text-orange-600">İşletim Sistemleri</span></h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">İhtiyacınıza göre Linux ve Windows seçenekleri.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {OperatingSystems.map((os) => (
              <Card key={os.id} className="relative overflow-hidden border-0 shadow-lg">
                <CardContent className="relative p-6 text-center">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${os.color} flex items-center justify-center mx-auto mb-4`}>
                    <os.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{os.name}</h3>
                  <div className="space-y-2">
                    {os.versions.map((version, idx) => (
                      <div key={idx} className="flex items-center justify-center space-x-2">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        <span className="text-sm text-gray-700">{version}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section 
        className="py-16 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Sıkça Sorulan <span className="text-orange-600">Sorular</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              VDS Sunucu ile ilgili merak ettiğiniz soruların cevaplarını burada bulabilirsiniz
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {(faqs.length ? faqs : fallbackFaqs).map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center">
                      <HelpCircle className="w-5 h-5 mr-3 text-orange-600" />
                      {faq.question}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* VDS Contact Section */}
      <section 
        className="py-20 bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 text-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4">VDS Sunucu İhtiyacınız İçin Bize Ulaşın</h2>
            <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
              Yüksek performanslı VDS sunucu çözümlerimiz hakkında detaylı bilgi almak ve size özel paket önerileri için ekibimizle iletişime geçin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="/iletisim"
                className="inline-flex items-center px-8 py-4 bg-white text-orange-600 rounded-lg hover:bg-gray-100 font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <Mail className="w-5 h-5 mr-2" />
                İletişime Geç
              </a>
              <a
                href={`https://api.whatsapp.com/send/?phone=${encodeURIComponent(whatsappNumber)}&text=${encodeURIComponent('Merhaba, VDS sunucu hizmetleriniz hakkında bilgi almak istiyorum.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white shadow-lg"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp İletişim
              </a>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Clock className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">7/24 Destek</h3>
                <p className="text-orange-100">VDS sunucu konusunda kesintisiz destek</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Shield className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Güvenli Altyapı</h3>
                <p className="text-orange-100">Dedike kaynaklarla yüksek performans</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <Award className="w-12 h-12 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Uzman Ekip</h3>
                <p className="text-orange-100">VDS sunucu konusunda deneyimli ekibimiz</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const fallbackFaqs = [
  { question: 'VDS ile VPS Arasındaki Fark Nedir?', answer: 'VDS donanımsal olarak paylaşılan fiziki sunucu üzerinde dedike kaynak sunar; VPS ise sanal kaynak paylaşımı ile daha ekonomik çözümler sağlar.' },
  { question: 'VDS Sunucuya Panel Kurulabilir mi?', answer: 'Evet. cPanel, Plesk veya CyberPanel gibi paneller kurulabilir.' },
  { question: 'VDS Sunucularda Yedekleme Nasıl Yapılır?', answer: 'Snapshot ve periyodik yedekleme ile hızlı geri dönüş gerçekleştirilir.' },
];

export default VDSServerPage;


